import { useContext, useEffect, useRef, useState } from 'react';
import { AccountCircle, SentimentSatisfiedAlt } from '@mui/icons-material';
import { Section, SECTION_TYPE } from 'atoms/Section';
import { AttachFileButton } from 'atoms/AttachFileButton';
import { cvtDatetime } from 'commons/utils';
import { fetchMessages, Message } from 'hooks/u011/useGetMessagesHooks';
import { postAttachedFile } from 'hooks/u011/usePostAttachedFileHooks';
import { putMessages } from 'hooks/u011/usePutMessagesHooks';
import { AppContext } from 'templates/AppContextProvider';
import { CreateMessageArea } from './CreateMessageArea';
import { useTranslationWithLang } from 'hooks/useTranslationWithLang';
import LoadingModal from 'organisms/LoadingModal';
import { RefetchButton } from 'atoms/RefetchButton';

type Props = {
  userId: string;
  auditionId: string;
  setAuditionTitle: (title: string) => void;
  catchError: (err: string) => void;
};
const REFETCH_INTERVAL = 10;

export const MessageSection = (props: Props) => {
  const ctx = useContext(AppContext);
  const messageListRef = useRef<HTMLUListElement>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [showRefetchButton, setShowRefetchButton] = useState(true);

  const { t: tMessage } = useTranslationWithLang('message');
  const { t: tCommon } = useTranslationWithLang('commons');

  useEffect(() => {
    if (props.userId && props.auditionId) {
      fetchData();
    }
  }, [props.userId, props.auditionId]);

  const getAttachFile = async (filePath: string, orignalFileName: string | null) => {
    setLoading(true);
    postAttachedFile(props.userId, props.auditionId, filePath, orignalFileName)
      .then((res) => {
        if (!res) {
          props.catchError(tMessage('MU005F0111E'));
        }
      })
      .catch((err) => {
        console.error(err);
        props.catchError(tMessage('MU005F0111E'));
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const originator = (message: Message) => {
    const dom = message.isUser ? (
      <>
        <SentimentSatisfiedAlt />
        <p className="pl-1 whitespace-nowrap">{tCommon('TU000_029')}</p>
      </>
    ) : (
      <>
        <AccountCircle />
        <p className="pl-1 whitespace-nowrap">{tCommon('TU000_030')}</p>
      </>
    );
    const InputFileButton =
      message.file == null || message.file === '' ? (
        <></>
      ) : (
        <AttachFileButton
          onClick={() => message.file && getAttachFile(message.file, message.file_name)}
        ></AttachFileButton>
      );
    return (
      <div
        className="flex flex-wrap items-center gap-1"
        style={{
          color: `var(${message.isUser ? '--c-message-user-color' : '--c-message-color'})`,
        }}
      >
        <div className="flex items-center">{dom}</div>
        <p className="whitespace-nowrap">{cvtDatetime(message.ins_dtm)}</p>
        {message.file && message.file !== '' && (
          <div className="whitespace-nowrap">{InputFileButton}</div>
        )}
      </div>
    );
  };

  const messageList = (messages: Message[]) => {
    const domList = [];
    if (messages != null) {
      const counter = messages.length;
      if (counter > 0) {
        [...messages].reverse().forEach((message, i) => {
          domList.push(
            <li key={i} className="mb-2">
              {originator(message)}
              <div className="whitespace-pre-wrap">{message.message}</div>
            </li>
          );
        });
      } else if (loading) {
        domList.push(<li key="loading"></li>);
      } else {
        domList.push(<li key="not_found">{tMessage('MU011F0060I')}</li>);
      }
    }
    return domList;
  };

  const fetchData = () => {
    setLoading(true);
    fetchMessages(props.userId, props.auditionId, ctx.currentLang)
      .then((res) => {
        if (res != null) {
          setMessages(res.messages);
          props.setAuditionTitle(res.audition_title);
          putMessages(props.userId, props.auditionId)
            .then((res) => {
              if (res && res.status === '99') {
                console.error(res.msg);
                props.catchError(tMessage('MU011F0059E'));
                setShowRefetchButton(false);
              }
            })
            .catch((err) => {
              console.error(err);
              props.catchError(tMessage('MU011F0059E'));
              setShowRefetchButton(false);
            });
        }
      })
      .catch((err) => {
        console.error(err);
        props.catchError(tMessage('MU011F0059E'));
        setShowRefetchButton(false);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleRefetchClick = () => {
    setShowRefetchButton(false);
    fetchData();
    setTimeout(() => setShowRefetchButton(true), 1000 * REFETCH_INTERVAL);
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (messageListRef.current) {
      messageListRef.current.scrollTop = messageListRef.current.scrollHeight;
    }
  }, [messages]);

  return (
    <>
      {loading && <LoadingModal />}
      <Section type={SECTION_TYPE.MESSAGE} className="flex flex-col h-full">
        <div className="flex flex-col h-full overflow-hidden break-words whitespace-pre-wrap">
          <ul ref={messageListRef} className="h-full flex flex-col overflow-y-auto py-6 pl-6 pr-6">
            {messageList(messages)}
            {showRefetchButton && (
              <li style={{ listStyleType: 'none' }} className="flex justify-center">
                <RefetchButton
                  onClick={handleRefetchClick}
                  className="self-center"
                  ariaLabel={tCommon('TU000_078')}
                />
              </li>
            )}
          </ul>
          <div className="w-full justify-center items-center p-6">
            <CreateMessageArea
              userId={props.userId}
              auditionId={props.auditionId}
              catchError={props.catchError}
              onMessageSent={fetchData}
              loading={setLoading}
            />
          </div>
        </div>
      </Section>
    </>
  );
};
